/**
 * Centralised React Query keys for the Kai-API.
 *
 * Co-locating these prevents drift between the producer (useQuery) and any
 * cache-invalidation calls (queryClient.invalidateQueries). Always go
 * through this module -- never inline string-keyed arrays.
 */

import type { MyAssignmentsFilters } from '@/api/curate';
import type { DashboardFilters } from '@/api/dashboard';

export const queryKeys = {
  all: ['kai'] as const,

  categoryScores: (filters?: DashboardFilters) =>
    ['kai', 'category-scores', filters ?? {}] as const,

  categoryTrends: (filters?: DashboardFilters) =>
    ['kai', 'category-trends', filters ?? {}] as const,

  domainRank: (domain: string, filters?: DashboardFilters) =>
    ['kai', 'rank', domain, filters ?? {}] as const,

  chiefComplaints: (filters?: DashboardFilters) =>
    ['kai', 'chief-complaints', filters ?? {}] as const,

  chiefComplaint: (id: string, filters?: DashboardFilters) =>
    ['kai', 'chief-complaints', id, filters ?? {}] as const,

  complaintIndicators: (id: string, filters?: DashboardFilters) =>
    ['kai', 'chief-complaints', id, 'indicators', filters ?? {}] as const,

  indicatorDescriptions: () => ['kai', 'indicator-descriptions'] as const,

  subcategoryScores: (parentId: string, filters?: DashboardFilters) =>
    ['kai', 'subcategory-scores', parentId, filters ?? {}] as const,

  overallStats: (filters?: DashboardFilters) => ['kai', 'overall-stats', filters ?? {}] as const,

  indicatorPerformanceByMonth: (filters?: DashboardFilters) =>
    ['kai', 'indicator-performance-by-month', filters ?? {}] as const,

  recentActivity: (filters?: DashboardFilters & { limit?: number }) =>
    ['kai', 'recent-activity', filters ?? {}] as const,

  homeHighlights: (filters?: DashboardFilters & { limit?: number; minDenom?: number }) =>
    ['kai', 'home-highlights', filters ?? {}] as const,

  complaintTrends: (id: string, filters?: DashboardFilters) =>
    ['kai', 'chief-complaints', id, 'trends', filters ?? {}] as const,

  whyThisMatters: (id: string) => ['kai', 'chief-complaints', id, 'why-this-matters'] as const,

  complaintPlan: (id: string, filters?: DashboardFilters) =>
    ['kai', 'chief-complaints', id, 'plan', filters ?? {}] as const,

  plan: (filters?: DashboardFilters) => ['kai', 'plan', filters ?? {}] as const,

  domainSubdomainTrend: (domain: string, filters?: DashboardFilters) =>
    ['kai', 'domains', domain, 'subdomain-trend', filters ?? {}] as const,

  health: () => ['kai', 'health'] as const,

  myIdentity: () => ['kai', 'me'] as const,

  /* Curate ---------------------------------------------------------------- */

  /** Feature availability probe. Cheap, and gates every other Curate query. */
  curateStatus: () => ['kai', 'curate', 'status'] as const,

  /** The signed-in provider's assigned articles. */
  curateAssignments: (filters?: MyAssignmentsFilters) =>
    ['kai', 'curate', 'assignments', filters ?? {}] as const,

  /**
   * Everything the reader needs for one assignment. Keyed without filters so
   * progress and answer mutations can invalidate it precisely.
   */
  curateReader: (assignmentId: string) =>
    ['kai', 'curate', 'assignments', assignmentId, 'reader'] as const,
};
